import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Copy, Mail, Phone, Trash2, TrendingUp, Users, CheckCircle2, Sparkles, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { timeAgo, fullDate } from "@/lib/timeAgo";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { PagedToolbar, Pagination } from "@/components/shared/Pagination";
import { PlatformChip } from "@/components/shared/Chips";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

type Status = "new" | "contacted" | "converted" | "lost";

interface CapturedLead {
  lead_id: string;
  workspace_id: string;
  account_id: string;
  platform?: string | null;
  channel_display_name?: string | null;
  name: string;
  mobile: string;
  mobile_country?: string | null;
  email: string;
  message?: string | null;
  ref_comment_id?: string | null;
  ref_author_handle?: string | null;
  ref_post_id?: string | null;
  ref_post_url?: string | null;
  status: Status;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

interface Stats {
  total: number;
  new: number;
  converted: number;
  this_week: number;
  per_channel: { account_id: string; channel_display_name?: string; platform?: string; count: number }[];
}

const STATUS_OPTS: Status[] = ["new", "contacted", "converted", "lost"];

const STATUS_STYLES: Record<Status, string> = {
  new: "bg-violet-50 text-violet-700 border-violet-200",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-slate-100 text-slate-500 border-slate-200",
};

const KpiCard: React.FC<{
  label: string; value: React.ReactNode; icon: React.ReactNode; tone: string; testId: string;
}> = ({ label, value, icon, tone, testId }) => (
  <div className="sg-card p-4 flex items-center gap-3" data-testid={testId}>
    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}>{icon}</div>
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">{label}</div>
      <div className="text-2xl font-display font-bold leading-tight">{value}</div>
    </div>
  </div>
);

export const CapturedLeads: React.FC = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "staff" || !!user?.is_super_admin;
  const canDelete = user?.role === "admin" || !!user?.is_super_admin;
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "all";
  const accountId = params.get("account_id") || "all";
  const search = params.get("q") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const pageSize = Math.max(10, Math.min(500, parseInt(params.get("page_size") || "50", 10) || 50));

  const stats = useQuery<Stats>({
    queryKey: ["captured-leads-stats"],
    queryFn: async () => (await api.get("/captured-leads/stats")).data,
  });

  const leads = useQuery<{ items: CapturedLead[]; total: number; offset: number; limit: number }>({
    queryKey: ["captured-leads", status, accountId, search, page, pageSize],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (status !== "all") q.set("status", status);
      if (accountId !== "all") q.set("account_id", accountId);
      if (search.trim()) q.set("q", search.trim());
      q.set("offset", String((page - 1) * pageSize));
      q.set("limit", String(pageSize));
      return (await api.get(`/captured-leads?${q.toString()}`)).data;
    },
  });

  const items = leads.data?.items || [];
  const total = leads.data?.total || 0;

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<CapturedLead> }) =>
      (await api.patch(`/captured-leads/${id}`, patch)).data,
    onSuccess: () => {
      toast.success("Lead updated");
      qc.invalidateQueries({ queryKey: ["captured-leads"] });
      qc.invalidateQueries({ queryKey: ["captured-leads-stats"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/captured-leads/${id}`)).data,
    onSuccess: () => {
      toast.success("Lead deleted");
      qc.invalidateQueries({ queryKey: ["captured-leads"] });
      qc.invalidateQueries({ queryKey: ["captured-leads-stats"] });
    },
  });

  const setParam = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v === null || v === "" || v === "all") p.delete(k); else p.set(k, v);
    p.set("page", "1");
    setParams(p);
  };

  const copy = async (text: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <DashboardLayout
      title="Lead Capture"
      subtitle="Real contacts captured from social replies — turn conversations into customers."
      seoPath="/lead-capture"
      seoDescription="Real contact details captured from your social channels — name, phone, email — ready for sales follow-up."
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="lc-kpis">
        <KpiCard
          testId="lc-kpi-total"
          label="Total captured" tone="bg-violet-100 text-violet-700"
          icon={<Users className="h-5 w-5" />}
          value={stats.data?.total ?? "—"}
        />
        <KpiCard
          testId="lc-kpi-new"
          label="New" tone="bg-fuchsia-100 text-fuchsia-700"
          icon={<Sparkles className="h-5 w-5" />}
          value={stats.data?.new ?? "—"}
        />
        <KpiCard
          testId="lc-kpi-week"
          label="This week" tone="bg-blue-100 text-blue-700"
          icon={<TrendingUp className="h-5 w-5" />}
          value={stats.data?.this_week ?? "—"}
        />
        <KpiCard
          testId="lc-kpi-converted"
          label="Converted" tone="bg-emerald-100 text-emerald-700"
          icon={<CheckCircle2 className="h-5 w-5" />}
          value={stats.data?.converted ?? "—"}
        />
      </div>

      <PagedToolbar
        testId="lc-search"
        search={search}
        placeholder="Search by name, email, mobile or message…"
        onSearch={(v) => setParam("q", v)}
      />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={status} onValueChange={(v) => setParam("status", v)}>
          <SelectTrigger data-testid="lc-filter-status" className="w-44 bg-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        {stats.data?.per_channel && stats.data.per_channel.length > 1 && (
          <Select value={accountId} onValueChange={(v) => setParam("account_id", v)}>
            <SelectTrigger data-testid="lc-filter-channel" className="w-64 bg-white">
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All channels</SelectItem>
              {stats.data.per_channel.map((ch) => (
                <SelectItem key={ch.account_id} value={ch.account_id}>
                  {ch.channel_display_name || ch.account_id} ({ch.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {leads.isLoading ? (
        <div className="sg-skeleton h-64" />
      ) : items.length > 0 ? (
        <div className="grid gap-3">
          {items.map((l) => (
            <div key={l.lead_id} className="sg-card p-4" data-testid={`captured-row-${l.lead_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-sm">{l.name}</span>
                    <span className={`sg-chip border ${STATUS_STYLES[l.status]}`}>{l.status}</span>
                    {l.platform && <PlatformChip platform={l.platform} />}
                    {l.channel_display_name && (
                      <span className="text-[11px] text-[color:var(--sg-muted)]">via {l.channel_display_name}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-x-4 gap-y-1 text-xs text-[color:var(--sg-fg)] flex-wrap">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-[color:var(--sg-primary)]" data-testid={`captured-email-${l.lead_id}`}>
                      <Mail className="h-3 w-3" /> {l.email}
                    </a>
                    <a href={`tel:${l.mobile}`} className="inline-flex items-center gap-1 hover:text-[color:var(--sg-primary)]" data-testid={`captured-mobile-${l.lead_id}`}>
                      <Phone className="h-3 w-3" /> {l.mobile}
                    </a>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                      onClick={() => copy(`${l.name}\n${l.email}\n${l.mobile}`, "Contact copied")}
                      data-testid={`captured-copy-${l.lead_id}`}
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                  {l.message && (
                    <p className="text-xs mt-2 text-[color:var(--sg-fg)] bg-[color:var(--sg-muted-bg)] rounded-md p-2.5">
                      {l.message}
                    </p>
                  )}
                  {l.ref_author_handle && (
                    <div className="text-[11px] text-[color:var(--sg-muted)] mt-2 inline-flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" /> From reply to {l.ref_author_handle}
                      </span>
                      {l.ref_post_url && (
                        <a
                          href={l.ref_post_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1"
                          data-testid={`captured-post-link-${l.lead_id}`}
                        >
                          View original post ↗
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select
                    value={l.status}
                    onValueChange={(v) => update.mutate({ id: l.lead_id, patch: { status: v as Status } })}
                    disabled={!canEdit}
                  >
                    <SelectTrigger className="w-36 h-8 bg-white text-xs disabled:opacity-60" data-testid={`captured-status-${l.lead_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {STATUS_OPTS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-8 w-8 p-0"
                      onClick={() => {
                        if (window.confirm(`Delete lead from ${l.name}?`)) remove.mutate(l.lead_id);
                      }}
                      data-testid={`captured-delete-${l.lead_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-[color:var(--sg-muted)] mt-2" title={fullDate(l.created_at)}>
                Captured {timeAgo(l.created_at)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title={search || status !== "all" ? "No captured leads match your filters" : "No captured leads yet"}
          description={
            search || status !== "all"
              ? "Try clearing the filters or search term."
              : "Send a capture link in reply to your best comments or complaints — the submissions will show up here."
          }
        />
      )}

      <Pagination
        testId="captured-pagination"
        page={page} pageSize={pageSize} total={total}
        onPageChange={(p) => { const q = new URLSearchParams(params); q.set("page", String(p)); setParams(q); }}
        onPageSizeChange={(s) => { const q = new URLSearchParams(params); q.set("page_size", String(s)); q.set("page", "1"); setParams(q); }}
      />
    </DashboardLayout>
  );
};

export default CapturedLeads;
